import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import Accordion from 'components/Accordion'
import EcoSystemAccordion from './EcosystemAccordion'
import EcosystemAccordionContent from './EcosystemAccordionContent'
import Select from 'components/Select'
import Loader from 'components/Loader'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import Pagination from 'features/Pagination'
import Checkbox from 'components/Checkbox'
import Button from 'components/Button'
import { FormattedMessage } from 'react-intl'
import { PaginationWrapper } from 'components/BiodiversitySelector/style'
import { SpeciesList, BulkActionsWrapper } from './style'
import { getCopyPayload } from './ecosystemCopyStore'
import {
  requestThreatScope,
  requestThreatSeverity,
  requestAllThreatTriggers,
  requestDeleteSpeciesThreat,
  requestDeleteMonitorThreatTriggers,
  requestPressureCopyPaste
} from 'api/monitoring'
import { siteThreatsTiming } from 'constants/siteThreatsTiming'
import postRequest from 'utils/postRequest'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import Modal from 'components/Modal'
import FlowRequestModal from 'components/FlowRequestModal'
import { PageSubtitle, TooltipHintWrapper, PageSubtitleWrapper } from 'flow/SiteProposal/pages/About/style'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'

/* Keep your original filter that preserves hierarchy */
const flattenTree = (nodes, filter, depth = 0) => {
  return nodes.flatMap((node) => {
    const matchesFilter = filter === 'all' || node.kind === filter
    const children = node.children ? flattenTree(node.children, filter, depth + 1) : []
    const includeNode = matchesFilter || children.length
    if (!includeNode) {
      return []
    }
    return [
      {
        ...node,
        children,
        depth
      }
    ]
  })
}

/* helpers for building lookup maps and collecting ids */
const buildMaps = (nodes) => {
  const idToNode = new Map()
  const idToParent = new Map()
  const walk = (list, parent = null) => {
    list.forEach((node) => {
      idToNode.set(node.id, node)
      if (parent) idToParent.set(node.id, parent.id)
      if (node.children && node.children.length) walk(node.children, node)
    })
  }
  walk(nodes, null)
  return { idToNode, idToParent }
}

const collectDescendantIds = (node) => {
  const ids = []
  const walk = (n) => {
    ids.push(n.id)
    if (n.children && n.children.length) {
      n.children.forEach((c) => walk(c))
    }
  }
  walk(node)
  return ids
}
const collectAncestorIds = (id, idToParent) => {
  const ancestors = []
  let current = idToParent.get(id)
  while (current) {
    ancestors.push(current)
    current = idToParent.get(current)
  }
  return ancestors
}

const bulkActionOptions = [
  { label: 'Paste analysis', value: 'bulk-paste' },
  { label: 'Delete', value: 'bulk-delete' }
]

const EcosystemsTabContent = ({
  preselectedItems,
  onSelectionChange,
  threatItemId,
  items,
  accordionKey,
  handleToggleNode,
  openDetailsModal,
  handleCloneNode,
  isAllSelected,
  handleSelectAll,
  displayOptions = [],
  pagination = { currentPage: 1, resultsPerPage: 12 },
  setPagination,
  totalItems = 0,
  totalPages = 1,
  isEditable,
  isScopeOptions,
  isSeverityOptions
}) => {
  const params = useParams()
  const [allEcosystems, setAllEcosystems] = useState([])
  const [serverOverridesPreselected, setServerOverridesPreselected] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [loadingAll, setLoadingAll] = useState(false)
  const [isTimingOptions, setIsTimingOptions] = useState([])
  const [timingSelections, setTimingSelections] = useState({})
  const [typeFilter, setTypeFilter] = useState(displayOptions[1])
  const [scopeSelections, setScopeSelections] = useState({})
  const [severitySelections, setSeveritySelections] = useState({})
  const [removedNodeIds, setRemovedNodeIds] = useState(new Set())
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [internalPagination, setInternalPagination] = useState(pagination)
  const [bulkAction, setBulkAction] = useState(null)
  const [isModalForBulkDeletingOpen, setIsModalForBulkDeletingOpen] = useState(false)

  const preselectedCount = (() => {
    if (!preselectedItems) return 0
    if (Array.isArray(preselectedItems)) return preselectedItems.length
    if (preselectedItems && Array.isArray(preselectedItems.data)) return preselectedItems.data.length
    return 0
  })()

  const getSourceArray = () => {
    if (preselectedCount > 0 && !serverOverridesPreselected) {
      let preArr = Array.isArray(preselectedItems) ? preselectedItems : (preselectedItems.data || [])
      let serverArr = []
      if (Array.isArray(allEcosystems)) serverArr = allEcosystems
      else if (allEcosystems && Array.isArray(allEcosystems.data)) serverArr = allEcosystems.data
      if (serverArr && serverArr.length > 0) {
        const map = new Map()
        // prefer server entries when ids collide
        serverArr.forEach((s) => {
          const id = s && (s.id || s.historyId) ? (s.id || s.historyId) : JSON.stringify(s)
          map.set(id, s)
        })
        preArr.forEach((p) => {
          const id = p && (p.id || p.historyId) ? (p.id || p.historyId) : JSON.stringify(p)
          if (!map.has(id)) map.set(id, p)
        })
        return Array.from(map.values())
      }
      return preArr
    }
    if (allEcosystems) {
      if (Array.isArray(allEcosystems)) return allEcosystems
      if (Array.isArray(allEcosystems.data)) return allEcosystems.data
      // if it's an object with other values try to find an array inside
      const vals = Object.values(allEcosystems).find((v) => Array.isArray(v))
      if (Array.isArray(vals)) return vals
    }
    if (Array.isArray(items)) return items
    return []
  }

  // Normalize incoming items so this component can accept preselected payloads
  const normalizedItems = useMemo(() => {
    const source = getSourceArray()

    return source.map((it, idx) => {
      // If the item already looks like a node, keep it
      if (it && it.id && it.name) return it
      // If payload from preselected uses taxonomicGroup / data shape
      if (it && (it.taxonomicGroup || it.name)) {
        const name = it.taxonomicGroup || it.name || `item-${idx}`
        const id = it.id || (`group-${name.replace(/\s+/g, '-').toLowerCase()}`)
        const children = Array.isArray(it.species)
          ? it.species.map((s, si) => ({ id: s.historyId || s.id || `s-${idx}-${si}`, name: s.scientificName || s.name || `species-${si}`, kind: 'species', raw: s }))
          : (it.children || [])
        return { ...it, id, name, kind: it.kind || 'ecosystem', children }
      }
      // fallback: synthesize an id/name
      const name = (it && (it.name || it.label)) || `item-${idx}`
      const id = (it && (it.id || it.historyId)) || `node-${idx}`
      return { ...it, id, name, kind: it.kind || 'ecosystem', children: it.children || [] }
  })
  }, [items, preselectedCount, preselectedItems, allEcosystems, serverOverridesPreselected, refreshKey])

  const [localSelectedIds, setLocalSelectedIds] = useState(() => new Set())
  const initialItemsRef = useRef(null)

  // Initialize local selection when component receives normalized items and parent hasn't provided selection
  const initializedRef = useRef(false)
  // If navigation provides an authoritative server payload, clear any local removed node set
  useEffect(() => {
    try {
      if (preselectedItems && preselectedItems.skipRemovedFilter) {
        setRemovedNodeIds(new Set())
      }
    } catch (e) {}
  }, [preselectedItems])

  // If navigation provided an authoritative server payload, apply it as the
  // source so any previously-removed items are restored in the UI.
  useEffect(() => {
    try {
      if (preselectedItems && preselectedItems.skipRemovedFilter) {
        const arr = Array.isArray(preselectedItems.data) ? preselectedItems.data : (Array.isArray(preselectedItems) ? preselectedItems : [])
        if (arr && arr.length > 0) {
          setAllEcosystems(arr)
          setServerOverridesPreselected(true)
          setRemovedNodeIds(new Set())
          setSelectedIds(new Set())
          setRefreshKey((k) => k + 1)
        }
      }
    } catch (e) {}
  }, [preselectedItems])
  useEffect(() => {
    try {
      if (initializedRef.current) return
      if ((!selectedIds || (selectedIds && selectedIds.size === 0)) && normalizedItems && normalizedItems.length > 0) {
        const s = new Set()
        // prefer explicit checked flags in raw payload when available
        normalizedItems.forEach((n) => {
          const raw = n.raw || {}
          // const isChecked = raw && (raw.isChecked === true || raw.checked === true || raw.selected === true)
          const isChecked = raw
          if (isChecked) s.add(n.id)
        })
        // if none explicitly checked, select all incoming items by default
        if (s.size === 0) normalizedItems.forEach((n) => s.add(n.id))
        setLocalSelectedIds(s)
        initializedRef.current = true
      }
    } catch (e) {}
  }, [normalizedItems, selectedIds])

  const effectiveSelectedIds = (selectedIds && selectedIds.size > 0) ? selectedIds : localSelectedIds

  // Cache the first non-empty normalized items so transient empty updates don't clear the UI
  useEffect(() => {
    try {
      if (initialItemsRef.current) return
      if (normalizedItems && normalizedItems.length > 0) {
        initialItemsRef.current = normalizedItems
      }
    } catch (e) {}
  }, [normalizedItems])

  // If we've fetched ecosystems and there are none for this threat, clear the cached initial items
  useEffect(() => {
    try {
      const noPreselected = preselectedCount === 0
      // derive fetched array from various shapes the API may return
      let fetchedArray = []
      if (Array.isArray(allEcosystems)) fetchedArray = allEcosystems
      else if (allEcosystems && Array.isArray(allEcosystems.data)) fetchedArray = allEcosystems.data
      else if (allEcosystems && typeof allEcosystems === 'object') {
        const maybe = Object.values(allEcosystems).find((v) => Array.isArray(v))
        if (Array.isArray(maybe)) fetchedArray = maybe
      }
      const fetchedEmpty = Array.isArray(fetchedArray) && fetchedArray.length === 0
      if (noPreselected && fetchedEmpty) {
        initialItemsRef.current = null
      }
    } catch (e) {}
  }, [preselectedCount, allEcosystems])

  // Derive the source array used for this render and avoid falling back to cached
  // initial items when the current source is empty (ensures "No records found" shows).
  const sourceArray = getSourceArray()
  const effectivePagination = (pagination && typeof setPagination === 'function') ? pagination : internalPagination
  const safeSetPagination = (updater) => {
    if (typeof setPagination === 'function') return setPagination(updater)
    return setInternalPagination((prev) => (typeof updater === 'function' ? updater(prev) : updater))
  }
  const displayItems = (Array.isArray(sourceArray) && sourceArray.length === 0)
    ? []
    : ((normalizedItems && normalizedItems.length > 0) ? normalizedItems : (initialItemsRef.current || []))

  // filter out any nodes that were removed locally so UI updates immediately
  const filterRemoved = (nodes, removedSet) => {
    if (!Array.isArray(nodes)) return []
    return nodes.reduce((acc, node) => {
      if (removedSet && removedSet.has && removedSet.has(node.id)) return acc
      const children = node.children ? filterRemoved(node.children, removedSet) : []
      acc.push({ ...node, children })
      return acc
    }, [])
  }

  const visibleItems = useMemo(() => filterRemoved(displayItems, removedNodeIds), [displayItems, removedNodeIds])

  // Derive pagination totals from available sources (parent props, server payloads, or local arrays)
  const effectiveTotalItems = useMemo(() => {
    // prefer explicit prop passed from parent
    if (typeof totalItems === 'number' && totalItems > 0) return totalItems
    // prefer pagination info from fetched allEcosystems
    const fromAll = allEcosystems && allEcosystems.pagination && Number(allEcosystems.pagination.totalNumber)
    if (fromAll) return fromAll
    // prefer pagination info from preselectedItems (only if server data does NOT override it)
    const fromPre =
      !serverOverridesPreselected &&
      preselectedItems &&
      preselectedItems.pagination &&
      Number(preselectedItems.pagination.totalNumber)
    if (fromPre) return fromPre
    // if allEcosystems is a plain array, use its length (including 0 for empty arrays)
    if (Array.isArray(allEcosystems)) return allEcosystems.length
    // fall back to source array length (server or incoming payload)
    if (Array.isArray(sourceArray)) return sourceArray.length
    // final fallback: displayItems length
    if (Array.isArray(displayItems)) return displayItems.length
    return 0
  }, [totalItems, preselectedItems, allEcosystems, sourceArray, displayItems, serverOverridesPreselected])

  const effectiveTotalPages = useMemo(() => {
    if (typeof totalPages === 'number' && totalPages > 0) return totalPages
    // prefer pagination from fetched allEcosystems
    const fromAll = allEcosystems && allEcosystems.pagination && Number(allEcosystems.pagination.totalPages)
    if (fromAll) return Math.max(1, fromAll)
    // prefer pagination from preselected (only if server data does NOT override it)
    const fromPre =
      !serverOverridesPreselected &&
      preselectedItems &&
      preselectedItems.pagination &&
      Number(preselectedItems.pagination.totalPages)
    if (fromPre) return Math.max(1, fromPre)
    // compute from items
    const perPage = (pagination && pagination.resultsPerPage) || 12
    return Math.max(1, Math.ceil(effectiveTotalItems / perPage))
  }, [totalPages, preselectedItems, allEcosystems, effectiveTotalItems, pagination, serverOverridesPreselected])

  const NoHeader = () => null

  useEffect(() => {
    const timingSource = Array.isArray(siteThreatsTiming) ? siteThreatsTiming : []
    const formattedTimingOptions = timingSource.map((item) => ({ value: item.id, label: item.name }))
    setIsTimingOptions(formattedTimingOptions)
    // All fetching of ecosystems is handled in a dedicated effect to avoid races
  }, [preselectedItems, params.id])

  // Build filtered tree from the already-normalized items so ids match
  const filteredTree = useMemo(() => {
    return flattenTree(normalizedItems || [], 'all')
  }, [normalizedItems])

  /* build fast lookup maps from the filtered hierarchical tree */
  const { idToNode, idToParent } = useMemo(() => buildMaps(filteredTree), [filteredTree])

  useEffect(() => {
    if (!idToNode || idToNode.size === 0) return

    const threatIdToMatch = preselectedItems?.threatItemId?.threatItemId
      ? Number(preselectedItems?.threatItemId?.threatItemId)
      : null
    const newScope = {}
    const newSeverity = {}
    const newTiming = {}

    for (const [nodeId, node] of idToNode.entries()) {
      if (!node || !node) continue
      const raw = node || {}
      // support multiple possible field names and array/object shapes
      const triggers =
        raw.monitor_threats_trigger ||
        raw.monitor_threats_triggers ||
        raw.monitorThreatsTrigger ||
        raw.monitorThreatsTriggers ||
        raw.trigger ||
        raw.triggers
      if (!triggers) continue
      const list = Array.isArray(triggers) ? triggers : [triggers]

      // try to find the trigger that matches the current threat id (monitor_threats_id)
      let match = null
      if (threatIdToMatch != null) {
        match = list.find((t) => {
          const mtid = t && (t.monitor_threats_id || t.monitorThreatsId || t.monitor_threats_id || t.monitorThreatsId)
          return mtid != null && Number(mtid) === threatIdToMatch
        })
      }
      if (!match) match = list[0]
      if (!match) continue

      if (match.scope_id !== undefined) newScope[nodeId] = match.scope_id
      if (match.severity_id !== undefined) newSeverity[nodeId] = match.severity_id
      if (match.site_threat_timing_id !== undefined && match.site_threat_timing_id !== null)
        newTiming[nodeId] = match.site_threat_timing_id

      const returnedTriggerId =
        match.id ||
        match.monitor_threats_triggers_id ||
        match.monitorThreatsTriggersId ||
        match.triggerId ||
        match.monitor_threats_triggers_id
      if (returnedTriggerId) {
        node.raw = { ...(node.raw || {}), monitor_threats_triggers_id: returnedTriggerId }
      }
    }

    // seed selection states so UI reflects existing trigger data
    if (Object.keys(newScope).length > 0) setScopeSelections((prev) => ({ ...newScope }))
    if (Object.keys(newSeverity).length > 0) setSeveritySelections((prev) => ({ ...newSeverity }))
    if (Object.keys(newTiming).length > 0) setTimingSelections((prev) => ({ ...newTiming }))
  }, [idToNode, preselectedItems])

  // robust node lookup: try direct map, then fall back to common alternate id fields
  const getNodeById = (searchId) => {
    try {
      if (!searchId) return null
      if (idToNode && idToNode.get) {
        const direct = idToNode.get(searchId)
        if (direct) return direct
      }
      if (idToNode && idToNode.size) {
        for (const node of idToNode.values()) {
          if (!node) continue
          if (node.id && node.id === searchId) return node
          if (node.id && node.id === searchId) return node
          const raw = node || {}
          if (raw.id && raw.id === searchId) return node
          if (raw.historyId && raw.historyId === searchId) return node
          if (raw.id && raw.id === searchId) return node
        }
      }
    } catch (e) {
      // ignore
    }
    return null
  }

  const updateScope = useCallback(
    (nodeId, value) => {
      setScopeSelections((prev) => ({ ...prev, [nodeId]: value }))
      const node = getNodeById(nodeId)
      if (!node) return
      const numeric = value && typeof value === 'object' && value.value !== undefined ? value.value : value
      const raw = node || {}

      const monitorId = Number((threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null)
      const body = [
        {
          monitor_threats_id: monitorId || null,
          siteEcosystemId: node.id || null,
          scope_id: numeric,
          site_pressure_id: Number(params.pressureId) || null
        }
      ]
      if (monitorId) {
        postRequest(`/api/monitor-threats-triggers`, body)
          .then((resp) => {
            try {
              const created = Array.isArray(resp) ? resp[0] : resp
              const returnedId =
                created &&
                (created.monitor_threats_triggers_id ||
                  created.monitorThreatsTriggersId ||
                  created.id ||
                  created.monitor_threats_triggers_id)
              if (returnedId) {
                node.raw = { ...(node.raw || {}), monitor_threats_triggers_id: returnedId }
              }
            } catch (err) {
              console.error('Failed to persist created trigger id', err)
            }
          })
          .catch((e) => console.error(e))
      }
    },
    [idToNode, preselectedItems]
  )
  const updateSeverity = useCallback(
    (nodeId, value) => {
      setSeveritySelections((prev) => ({ ...prev, [nodeId]: value }))
      const node = getNodeById(nodeId)
      if (!node) return
      const numeric = value && typeof value === 'object' && value.value !== undefined ? value.value : value
      const raw = node || {}
      const monitorId = Number((threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null)
      const body = [
        {
          monitor_threats_id: monitorId || null,
          siteEcosystemId: node.id || null,
          severity_id: numeric,
          site_pressure_id: Number(params.pressureId) || null
        }
      ]
      if (monitorId) {
        postRequest(`/api/monitor-threats-triggers`, body)
          .then((resp) => {
            try {
              const created = Array.isArray(resp) ? resp[0] : resp
              const returnedId =
                created &&
                (created.monitor_threats_triggers_id ||
                  created.monitorThreatsTriggersId ||
                  created.id ||
                  created.monitor_threats_triggers_id)
              if (returnedId) {
                node.raw = { ...(node.raw || {}), monitor_threats_triggers_id: returnedId }
              }
            } catch (err) {
              console.error('Failed to persist created trigger id', err)
            }
          })
          .catch((e) => console.error(e))
      }
    },
    [idToNode, preselectedItems]
  )
  const updateTiming = useCallback(
    (nodeId, option) => {
      setTimingSelections((prev) => ({ ...prev, [nodeId]: option }))
      let node = getNodeById(nodeId)
      const numeric = option && typeof option === 'object' && option.value !== undefined ? option.value : option
      const raw = node || {}
      const monitorId = Number((threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null)
      const body = [
        {
          monitor_threats_id: monitorId || null,
          siteEcosystemId: node.id || null,
          site_threat_timing_id: numeric,
          site_pressure_id: Number(params.pressureId) || null
        }
      ]
      if (monitorId) {
        postRequest(`/api/monitor-threats-triggers`, body)
          .then((resp) => {
            try {
              const created = Array.isArray(resp) ? resp[0] : resp
              const returnedId =
                created &&
                (created.monitor_threats_triggers_id ||
                  created.monitorThreatsTriggersId ||
                  created.id ||
                  created.monitor_threats_triggers_id)
              if (returnedId) {
                node = { ...(node || {}), monitor_threats_triggers_id: returnedId }
              }
            } catch (err) {
              console.error('Failed to persist created trigger id', err)
            }
          })
          .catch((e) => console.error(e))
      }
    },
    [idToNode, preselectedItems]
  )

  const deselectNodeAndDescendants = useCallback(
    (nodeId, set) => {
      const node = idToNode.get(nodeId)
      if (!node) return
      const desc = collectDescendantIds(node)
      desc.forEach((id) => set.delete(id))
    },
    [idToNode]
  )

  /* selection helpers for this tab (used when parent doesn't provide handlers) */
  const selectNodeAndDescendants = useCallback(
    (nodeId, set) => {
      const node = idToNode.get(nodeId)
      if (!node) return
      const desc = collectDescendantIds(node)
      desc.forEach((id) => set.add(id))
    },
    [idToNode]
  )

  const allIds = useMemo(() => {
    const ids = []
    const walk = (nodes) => {
      if (!Array.isArray(nodes)) return
      nodes.forEach((n) => {
        ids.push(n.id)
        if (n.children && n.children.length) walk(n.children)
      })
    }
    walk(visibleItems)
    return ids
  }, [visibleItems])

  const internalIsAllSelected = useMemo(
    () => allIds.length > 0 && allIds.every((id) => selectedIds.has(id)),
    [allIds, selectedIds]
  )

  const internalHandleSelectAll = useCallback(() => {
    if (internalIsAllSelected) {
      setSelectedIds(new Set())
      if (typeof handleSelectAll === 'function') handleSelectAll()
      return
    }
    setSelectedIds(new Set(allIds))
    if (typeof handleSelectAll === 'function') handleSelectAll()
  }, [internalIsAllSelected, allIds, handleSelectAll])

  const internalHandleToggleNode = useCallback(
    (node) => {
      if (!node) return
      const nodeId = node.id
      const next = new Set(selectedIds)
      const currentlySelected = next.has(nodeId)

      if (!currentlySelected) {
        // select node + descendants
        selectNodeAndDescendants(nodeId, next)
        // ensure ancestors selected when all descendants selected
        const ancestors = collectAncestorIds(nodeId, idToParent)
        ancestors.forEach((anc) => {
          const ancNode = idToNode.get(anc)
          if (!ancNode) return
          const desc = collectDescendantIds(ancNode)
          const speciesDesc = desc.filter((id) => {
            const n = idToNode.get(id)
            return n ? n.kind === 'species' : true
          })
          const allSelected = speciesDesc.length > 0 && speciesDesc.every((id) => next.has(id))
          if (allSelected) next.add(anc)
        })
      } else {
        // deselect node + descendants
        deselectNodeAndDescendants(nodeId, next)
        const ancestors = collectAncestorIds(nodeId, idToParent)
        ancestors.forEach((anc) => {
          const ancNode = idToNode.get(anc)
          if (!ancNode) return
          const ancDesc = collectDescendantIds(ancNode)
          const anySelected = ancDesc.some((id) => next.has(id))
          if (!anySelected) next.delete(anc)
        })
      }

      setSelectedIds(next)
    },
    [selectedIds, idToNode, idToParent, selectNodeAndDescendants, deselectNodeAndDescendants]
  )

  // when selection changes, notify parent if callback provided
  useEffect(() => {
    try {
      if (typeof onSelectionChange === 'function') {
        onSelectionChange({ ecosystems: selectedIds.size, selectedItems: Array.from(selectedIds) })
      }
    } catch (e) {}
  }, [selectedIds, onSelectionChange])

  const handleDeleteNode = useCallback(
    (node) => {
      if (!node) return
      const raw = node || {}
      // Attempt to derive the trigger id from several possible fields (prefer the explicit trigger id)
      const triggerCandidate =
        raw?.monitor_threats_triggers_id ||
        raw?.monitorThreatsTriggersId ||
        (raw?.monitor_threats_trigger && raw.monitor_threats_trigger[0] && raw.monitor_threats_trigger[0].id) ||
        raw?.triggerId ||
        raw?.id
      const triggerId = triggerCandidate === undefined || triggerCandidate === null ? null : (Number(triggerCandidate) || triggerCandidate)
      // Collect removed identifiers for parent component
      const removedGroupNames = []
      const removedSpeciesHistoryIds = []

      // If this is a group, collect group name
      if (node.kind === 'group') {
        removedGroupNames.push(node.name)
      } else if (node.kind === 'species') {
        // If this is a species, collect its history ID
        const historyId = node && (node.id)
        if (historyId) {
          removedSpeciesHistoryIds.push(historyId)
        }
      }

      const removeLocally = () => {
        setRemovedNodeIds((prev) => {
          const next = new Set(prev)
          next.add(node.id)
          // also add descendants to removed set
          const desc = collectDescendantIds(node)
          desc.forEach((d) => next.add(d))
          return next
        })

        // remove node and descendants from selection to update UI
        setSelectedIds((prev) => {
          const next = new Set(prev)
          deselectNodeAndDescendants(node.id, next)
          // also remove the node itself if present
          next.delete(node.id)
          return next
        })
        // persist removal for items that came from preselected payload so
        // they don't reappear after a browser refresh.
        try {
          const monitorId = Number((threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null)
          const storageKey = `deleted_preselected_pressure_${params.id || 'site'}_${params.monitoringId || 'mon'}_${monitorId || 'threat'}`
          const existing = JSON.parse(localStorage.getItem(storageKey) || '[]')
          const normalizedId = node.id && (String(node.id) || '')
          if (normalizedId && !existing.includes(normalizedId)) {
            existing.push(normalizedId)
            localStorage.setItem(storageKey, JSON.stringify(existing))
          }
        } catch (e) {}
      }

      if (!triggerId) {
        removeLocally()
        return
      }

      // call API then refresh server data and remove locally on success
     requestDeleteSpeciesThreat(triggerId)
        .then(() => {
          try {
            showSuccessToast('Selected item deleted successfully.')
          } catch (e) {}
          // after successful delete, fetch updated triggers for this monitorThreat
          const monitorThreatId = Number(
            (threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null
          )
          if (monitorThreatId) {
            requestAllThreatTriggers(monitorThreatId, 2)
              .then((resp) => {
                try {
                  const extractArrayFromResp = (r) => {
                    if (!r) return []
                    if (Array.isArray(r)) return r
                    if (r.data && Array.isArray(r.data)) return r.data
                    const seen = new Set()
                    const findArray = (obj) => {
                      if (!obj || typeof obj !== 'object') return null
                      if (seen.has(obj)) return null
                      seen.add(obj)
                      for (const k of Object.keys(obj)) {
                        const v = obj[k]
                        if (Array.isArray(v)) return v
                        if (v && typeof v === 'object') {
                          const nested = findArray(v)
                          if (nested) return nested
                        }
                      }
                      return null
                    }
                    const found = findArray(r)
                    return Array.isArray(found) ? found : []
                  }
                  const arr = extractArrayFromResp(resp)
                  const filtered = Array.isArray(arr)
                    ? arr.filter((it) => {
                        if (!it) return false
                        // compare common id fields to the node id
                        const idsToCheck = [it.id, it.historyId, it.siteEcosystemId, it.site_ecosystem_id]
                        return !idsToCheck.some((v) => v != null && String(v) === String(node.id))
                      })
                    : []
                  setAllEcosystems(filtered)
                  // force remount of Accordion to ensure authoritative server data is rendered
                  setRefreshKey((k) => k + 1)
                  setServerOverridesPreselected(true)
                  // clear removed/selected sets to reflect authoritative server result
                  setRemovedNodeIds(new Set())
                  setSelectedIds(new Set())
                  // Notify parent of the updated count
                  try {
                    if (typeof onSelectionChange === 'function') {
                      onSelectionChange({ ecosystems: filtered.length, selectedItems: [] })
                    }
                  } catch (e) {}
                  // clear any persisted deleted preselected ids for this monitor since
                  // server-provided data is now authoritative
                  try {
                    const monitorId = Number(
                      (threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null
                    )
                    const storageKey = `deleted_preselected_pressure_${params.id || 'site'}_${params.monitoringId || 'mon'}_${monitorId || 'threat'}`
                    localStorage.removeItem(storageKey)
                  } catch (e) {}
                } catch (e) {
                  // fallback: still remove locally
                  removeLocally()
                }
              })
              .catch((err) => {
                console.error('Failed to refresh triggers after delete', err)
                // fallback to local removal
                removeLocally()
              })
          } else {
            removeLocally()
          }
        })
        .catch((e) => {
          console.error('Failed to delete species threat', e)
        })
    },
    [deselectNodeAndDescendants, pagination, threatItemId, onSelectionChange, params]
  )

  const bulkPasteHandler = async (pastePayload) => {
      // collect selected ids (prefer raw.historyId/site id when available)
      const checkedSpeciesHistoryIds = Array.from(selectedIds).map((id) => {
        try {
          const node = idToNode.get(id)
          if (!node) return id
          const raw = node.raw || {}
          return raw.historyId || raw.siteSpeciesId || raw.site_species_id || node.id || id
        } catch (e) {
          return id
        }
      })
      // determine fully-checked groups (include group only when ALL its species descendants are selected)
      const checkedGroupNames = []

      if (bulkAction?.value === 'bulk-delete') {
        if (
          (!checkedSpeciesHistoryIds || checkedSpeciesHistoryIds.length === 0)
        ) {
          showErrorToast(<FormattedMessage id="beforeNotSelectBulkItemMessage" />)
          return
        } else {
          setIsModalForBulkDeletingOpen(true)
          return
        }
      } else if (bulkAction?.value === 'bulk-paste') {
        if (
          (!checkedSpeciesHistoryIds || checkedSpeciesHistoryIds.length === 0)
        ) {
          showErrorToast(<FormattedMessage id="triggerSpeciesNoSelectMessage" />)
          return
        }
        const payload = getCopyPayload()
        if (!payload || Object.keys(payload).length === 0) {
          showErrorToast(<FormattedMessage id="beforeNotSelectCopyPasteItemMessage" />)
          return
        }

        const finalPayload = {
          copy_from_ecosystem: payload?.copy_id || null,
          monitor_threats_id: payload?.monitor_threats_id,
          paste_to_ecosystem: checkedSpeciesHistoryIds,
          site_id: params.id || null,
          site_pressure_id: Number(params.pressureId) || null,
          filter_by: 2
        }
        // apply copy values to local selection states so UI reflects pasted values
        try {
          const scopeVal = payload && (payload.scope_id || payload.scope_id === 0) ? payload.scope_id : null
          const severityVal = payload && (payload.severity_id || payload.severity_id === 0) ? payload.severity_id : null
          const timingVal =
            payload && (payload.site_threat_timing_id || payload.site_threat_timing_id === 0)
              ? payload.site_threat_timing_id
              : null

          // update group + descendant species
          const newScope = { ...(scopeSelections || {}) }
          const newSeverity = { ...(severitySelections || {}) }
          const newTiming = { ...(timingSelections || {}) }

          if (idToNode && typeof idToNode.entries === 'function') {
            for (const [nid, node] of idToNode.entries()) {
              if (!node) continue
              // groups: check by name presence in checkedGroupNames
              if (node.kind === 'group' && checkedGroupNames.includes(node.name)) {
                if (scopeVal !== null) newScope[nid] = scopeVal
                if (severityVal !== null) newSeverity[nid] = severityVal
                if (timingVal !== null) newTiming[nid] = timingVal
                // also apply to descendant species
                const desc = collectDescendantIds(node)
                desc.forEach((d) => {
                  const dn = idToNode.get(d)
                  if (!dn) return
                  if (dn.kind === 'species') {
                    if (scopeVal !== null) newScope[d] = scopeVal
                    if (severityVal !== null) newSeverity[d] = severityVal
                    if (timingVal !== null) newTiming[d] = timingVal
                  }
                })
                continue
              }
              // species: match by historyId/site id or node id against checkedSpeciesHistoryIds
                const hist =
                  (node && node.id)
                if (checkedSpeciesHistoryIds.includes(hist) || checkedSpeciesHistoryIds.includes(node.id)) {
                  if (scopeVal !== null) newScope[nid] = scopeVal
                  if (severityVal !== null) newSeverity[nid] = severityVal
                  if (timingVal !== null) newTiming[nid] = timingVal
                }
            }
          }

          setScopeSelections(newScope)
          setSeveritySelections(newSeverity)
          setTimingSelections(newTiming)
        } catch (e) {
          // ignore local update failures
        }
        // remove unwanted keys from finalPayload before sending
        ;['scope_id', 'severity_id', 'site_threat_timing_id'].forEach((k) => {
          if (k in finalPayload) delete finalPayload[k]
        })
        //API call
        try {
          setPasteUsed(true)
        } catch (e) {}
        requestPressureCopyPaste(finalPayload)
          .then((response) => {
            //handle response
            showSuccessToast(<FormattedMessage id="pasteSuccessMessage" />)
          })
          .catch((error) => {
            showErrorToast(<FormattedMessage id="pasteFailureMessage" />)
            //handle error
            console.error('bulk handlePaste error: ', error)
          })
          .finally(() => {
            try {
              setPasteUsed(false)
            } catch (e) {}
          })
      }
    }

  async function bulkDeleteHandler() {
    // collect selected ids (prefer raw.historyId/site id when available)
    const checkedSpeciesHistoryIds = Array.from(selectedIds).map((id) => {
      try {
        const node = idToNode.get(id)
        if (!node) return id
        const raw = node.raw || {}
        return raw.historyId || raw.siteSpeciesId || raw.site_species_id || node.id || id
      } catch (e) {
        return id
      }
    })
    // determine fully-checked groups (include group only when ALL its species descendants are selected)
    const checkedGroupNames = []
    const finalPayload = {
      delete_ecosystems: checkedSpeciesHistoryIds,
      site_pressure_id: Number(params.pressureId) || null,
    }

    try {
      const res = await requestDeleteMonitorThreatTriggers(threatItemId, finalPayload)
      setIsModalForBulkDeletingOpen(false);
      try { resetStore() } catch (e) {}
      try {
        document.dispatchEvent(
          new CustomEvent('ecosystem:buttonToggled', { detail: { scope: 'header', id: null, button: null } })
        )
      } catch (e) {}
    } catch (e) {}
    try {
      setRemovedNodeIds((prev) => {
        const next = new Set(prev)
        // add any selectedIds directly so newly-added items (not yet in idToNode) are removed
        try {
          Array.from(selectedIds).forEach((sid) => next.add(sid))
        } catch (e) {}

        if (idToNode && typeof idToNode.entries === 'function') {
          for (const [nid, node] of idToNode.entries()) {
            if (!node) continue
            // group by name
            if (node.kind === 'group' && checkedGroupNames.includes(node.name)) {
              next.add(nid)
              const desc = collectDescendantIds(node)
              desc.forEach((d) => next.add(d))
              continue
            }
            if (node.kind === 'species') {
              const hist = (node.raw && (node.raw.historyId || node.raw.siteSpeciesId || node.raw.site_species_id || node.raw.id)) || node.id
              if (checkedSpeciesHistoryIds.includes(hist) || checkedSpeciesHistoryIds.includes(node.id)) {
                next.add(nid)
              }
            }
          }
        }
        return next
      })
    } catch (e) {}

    // clear current selection for deleted items
    try {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (idToNode && typeof idToNode.entries === 'function') {
          for (const [nid, node] of idToNode.entries()) {
            if (!node) continue
            const hist = (node.raw && (node.raw.historyId || node.raw.siteSpeciesId || node.raw.site_species_id || node.raw.id)) || node.id
            if (checkedSpeciesHistoryIds.includes(hist) || checkedSpeciesHistoryIds.includes(node.id) || (node.kind === 'group' && checkedGroupNames.includes(node.name))) {
              // remove node and its descendants from selection
              const desc = collectDescendantIds(node)
              desc.forEach((d) => next.delete(d))
              next.delete(nid)
            }
          }
        }
        return next
      })
    } catch (e) {}

    showSuccessToast(<FormattedMessage id="bulkDeleteToasterConfirmationMessage" />)

    // notify parent to re-fetch (server-side pagination / authoritative list)
    try {
      document.dispatchEvent(
        new CustomEvent('species:refresh', {
          detail: { currentPage: pagination.currentPage, resultsPerPage: pagination.resultsPerPage }
        })
      )
    } catch (e) {}

  }

  return (
    <div>
      {(!visibleItems || visibleItems.length > 0) && (
      <PageSubtitleWrapper style={{ marginTop: 20, justifyContent: 'flex-start', marginBottom: 0 }}>
        <PageSubtitle style={{ borderBottom: 'none', fontSize: '14px', fontWeight: 'normal' }}>
          <FormattedMessage id="threatTriggerSpecieSubTitle" />
        </PageSubtitle>
        <Tooltip
        placement="bottom"
        trigger={["hover"]}
        align={{ offset: [0, 16] }}
        zIndex={9999}
        overlay={{
          label: "threatTriggerSpecieSubTitle",
          infoTooltip: "threatTriggerSpecieInfoTooltip",
        }}
        type="big"
      >
        <TooltipHintWrapper>
          <InfoIcon />
        </TooltipHintWrapper>
        </Tooltip>
      </PageSubtitleWrapper>
      )}
      {(!loadingAll && visibleItems.length > 0) &&
        <BulkActionsWrapper>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                label={<FormattedMessage id="selectAll" defaultMessage="Select All" />}
                checked={internalIsAllSelected}
                onChange={internalHandleSelectAll}
                disabled={!isEditable}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Select
                className="bulk-select"
                marginBottom="0"
                value={bulkAction}
                onChange={(option) => setBulkAction(option)}
                placeholder="bulkAction"
                options={bulkActionOptions}
                disabled={!isEditable}
              />
              <Button marginTop="0" type={!bulkAction || !isEditable ? 'disabledPrimary' : 'primary'} isDisabled={!bulkAction || !isEditable} onClick={() => bulkPasteHandler(bulkAction.value)}>
                <FormattedMessage id="Apply" defaultMessage="Apply" />
              </Button>
            </div>
          </div>
        </BulkActionsWrapper>
      }

      <SpeciesList>
        {loadingAll ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#666' }}>
            <Loader />
          </div>
        ) : (!visibleItems || visibleItems.length === 0) ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#666' }}>
            <FormattedMessage id="noRecordsFound" defaultMessage="No records found" />
          </div>
        ) : (
          <Accordion
            key={`${accordionKey || 'accordion'}-${refreshKey}`}
            items={visibleItems}
            headerComponent={typeFilter && typeFilter.value === 'ecosystem' ? NoHeader : EcoSystemAccordion}
            headerProps={{
              getChecked: (item) => selectedIds.has(item.id),
              // inform header when we're viewing species-only
              isSpeciesView: typeFilter && typeFilter.value === 'ecosystem',
              onToggleNode: internalHandleToggleNode,
              getDepth: (item) => item.depth || 0,
              onOpenDetails: openDetailsModal,
              onClone: handleCloneNode,
              onDelete: handleDeleteNode,
              // pass selection/update handlers and option lists so header selects persist
              scopeSelections,
              severitySelections,
              timingSelections,
              updateTaxonGroupScope: updateScope,
              updateTaxonGroupSeverity: updateSeverity,
              updateTaxonGroupTiming: updateTiming,
              isScopeOptions,
              isSeverityOptions,
              isTimingOptions,
              isEditable
            }}
            initialActive={null}
            contentComponent={EcosystemAccordionContent}
            contentProps={{
              scopeSelections,
              severitySelections,
              timingSelections,
              updateScope,
              updateSeverity,
              updateTiming,
              isScopeOptions,
              isSeverityOptions,
              isTimingOptions,
              isSpeciesView: typeFilter && typeFilter.value === 'species',
              getChecked: (item) => selectedIds.has(item.id),
              onToggleNode: internalHandleToggleNode,
              getDepth: (item) => item.depth || 0,
              onOpenDetails: openDetailsModal,
              onClone: handleCloneNode,
              onDelete: handleDeleteNode,
              isEditable
            }}
          />
        )}
      </SpeciesList>

      {(effectiveTotalItems > 0) &&
      <PaginationWrapper>
        <ItemsPerPageDropdown
          resultsPerPage={effectivePagination.resultsPerPage}
          totalNumber={effectiveTotalItems}
          currentPage={effectivePagination.currentPage}
          onChange={(option) => safeSetPagination((prev) => ({ ...prev, resultsPerPage: option.value, currentPage: 1 }))}
        />
        {effectiveTotalPages > 1 && (
          <Pagination
            activePages={effectivePagination.currentPage}
            numberOfPages={effectiveTotalPages}
            nextPage={() =>
              safeSetPagination((prev) => ({ ...prev, currentPage: Math.min(effectiveTotalPages, prev.currentPage + 1) }))
            }
            prevPage={() => safeSetPagination((prev) => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
            selectPage={(page) => safeSetPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        )}
      </PaginationWrapper>
      }

      <Modal
        width="496px"
        isOpen={isModalForBulkDeletingOpen}
        toggleModal={setIsModalForBulkDeletingOpen}
        modalTitleId={'bulkDeleteModalTitle'}
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsModalForBulkDeletingOpen(false)
          }}
          confirmHandler={bulkDeleteHandler}
          confirmBtnText="yesDelete"
          text={<FormattedMessage id="bulkEcosystemDeleteModalMessage" />}
          item={''}
          approve={false}
        />
      </Modal>
    </div>
  )
}

export default EcosystemsTabContent
